import { useState, useMemo, useEffect } from 'react';
import { Box, Modal, IconButton } from '@mui/material';
import DocViewer, { DocViewerRenderers } from 'react-doc-viewer';
import Papa from 'papaparse';
import Button from '../button/button';
import Typography from '../typography/typography';
import { Close, Document, Eye } from '../icons';
import { getApiConfig } from '../../utils/api-config';

interface DocumentPreviewProps {
  fileUrl: string;
  fileName?: string;
}

const imageExts = ['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'bmp',
                   'tiff', 'tif', 'ico', 'heic', 'heif'];
const iframeExts = ['txt', 'csv', 'rtf'];
const pdfExts = ['pdf'];
const docViewerExts = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx',
                       'odt', 'ods', 'odp'];

const CsvPreview = ({ url }: { url: string }) => {
  const [data, setData] = useState<string[][]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(url).then(r => r.text()).then(text => {
      const result = Papa.parse(text);
      setData(result.data as string[][]);
      setLoading(false);
    });
  }, [url]);

  if (loading) return <Typography type='s3'>Loading...</Typography>;
  if (!data.length) return <Typography type='s3'>Empty file</Typography>;

  return (
    <Box component='table' sx={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          {(data[0] || []).map((h, i) => (
            <th key={i} style={{ border: '1px solid #ccc', padding: 8, background: '#f5f5f5', textAlign: 'left' }}>
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.slice(1).map((row, r) => (
          <tr key={r}>
            {row.map((c, i) => (
              <td key={i} style={{ border: '1px solid #ccc', padding: 8 }}>{c}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </Box>
  );
};

const TextPreview = ({ url }: { url: string }) => {
  const [text, setText] = useState('');

  useEffect(() => {
    fetch(url).then(r => r.text()).then(setText);
  }, [url]);

  return <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0, fontSize: 14 }}>{text}</pre>;
};

const DocumentPreview = ({ fileUrl, fileName }: DocumentPreviewProps) => {
  const [open, setOpen] = useState(false);
  const name = fileName || fileUrl?.substring(fileUrl?.lastIndexOf('/') + 1) || '';
  const ext = useMemo(() => name.split('.').pop()?.toLowerCase() || '', [name]);
  const s3BucketUrl = getApiConfig()?.s3BucketUrl || import.meta.env.VITE_S3_BUCKET_URL || '';
  const fullUrl = useMemo(() => 
    fileUrl?.startsWith('http') || fileUrl?.startsWith('blob:') 
      ? fileUrl 
      : s3BucketUrl + fileUrl, 
  [fileUrl, s3BucketUrl]);
  const isLocalFile = fileUrl?.startsWith('blob:');

  const renderContent = () => {
    if (imageExts.includes(ext)) {
      return (
        <img src={fullUrl} alt={name}
          style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
      );
    }

    if (pdfExts.includes(ext)) {
      return isLocalFile ? (
        <embed src={fullUrl} title={name} style={{ width: '100%', height: '100%' }} />
      ) : (
        <iframe src={fullUrl} title={name} style={{ width: '100%', height: '100%', border: 'none' }} />
      );
    }

    if (iframeExts.includes(ext)) {
      if (ext === 'csv') {
        return <CsvPreview url={fullUrl} />;
      }
      if (isLocalFile) {
        return <TextPreview url={fullUrl} />;
      }
      return (
        <iframe src={fullUrl} title={name} style={{ width: '100%', height: '100%', border: 'none' }} />
      );
    }

    if (isLocalFile) {
      return (
        <Box display='flex' alignItems='center' justifyContent='center'
          flexDirection='column' gap={2} height='100%'>
          <Document fontSize="large" />
          <Typography type='s3' weight='medium'>{name}</Typography>
          <Button variant='outlined' onClick={() => window.open(fullUrl, '_blank')}>
            Download
          </Button>
        </Box>
      );
    }

    return (
      <DocViewer
        documents={[{ uri: fullUrl, fileName: name, fileType: ext }]}
        pluginRenderers={DocViewerRenderers}
        config={{ header: { disableHeader: true } }}
        style={{ width: '100%', height: '100%' }}
      />
    );
  };

  return (
    <>
      <IconButton size='small' onClick={() => setOpen(true)}>
        <Eye fontSize='small' />
      </IconButton>

      <Modal open={open} onClose={() => setOpen(false)}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper', boxShadow: 24, borderRadius: 1, width: '90vw', height: '90vh',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}>
          <Box display='flex' justifyContent='space-between' alignItems='center' p={2}>
            <Typography type='s3' weight='medium'>{name}</Typography>
            <IconButton onClick={() => setOpen(false)}><Close /></IconButton>
          </Box>
          <Box flex={1} p={2} sx={{ overflow: 'auto', minHeight: 0 }}>
            {renderContent()}
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default DocumentPreview;
